import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import {
  Receipt as ReceiptIcon,
  Description as InvoiceIcon,
  CloudUpload as UploadIcon,
  Add as AddIcon,
  ArrowDownward as MoneyOutIcon,
  ArrowUpward as MoneyInIcon,
} from '@mui/icons-material';
import UploadCaptureControls from './UploadCaptureControls';
import ManualReceiptEntryDialog from './ManualReceiptEntryDialog';

type DocType = 'receipt' | 'invoice';

export interface EligibleDocument {
  id: string;
  contractor_name: string;
  invoice_date: string;
  total: string;
  document_type?: string;
  confirmed?: boolean;
}

/** A duplicate hit from onUploadNew could be a document in any state — not yet confirmed,
 * confirmed but previously removed from the transaction list, already visible, or already linked
 * as proof of a different real bank transaction — see AccountingPage's
 * handleUseDuplicateCashDocument for how each is resolved. */
export type DuplicateCashDoc = EligibleDocument & {
  excluded_from_transaction_list?: boolean;
  bank_transaction_id?: string | null;
  bank_statement_transaction_id?: string | null;
};

/** Outcome of uploading a brand-new receipt/invoice with no bank transaction behind it — mirrors
 * the three ways /api/receipts/upload / /api/invoices/upload can resolve, minus the "just worked"
 * case which the caller already turns into a plain success. */
export type UploadNewCashOutcome =
  | { status: 'success' }
  | { status: 'duplicate'; invoice: DuplicateCashDoc }
  | { status: 'manual_entry'; fileInfo: any; error: string | null };

export interface ManualCashEntry {
  date: string;
  description: string;
  total: string;
  direction: 'money_out' | 'money_in';
}

interface AddCashTransactionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Every already-uploaded receipt/invoice that isn't already visible as a transaction — both
   * ones still awaiting a "what is this?" decision (Pending confirmation) and ones already
   * confirmed but previously taken out of the transaction list via its own "Add to transactions"
   * toggle. See AccountingPage.tsx's eligibleDocsForNewTransaction. */
  eligibleDocuments: EligibleDocument[];
  /** Picked an existing document — the caller decides which real endpoint that means
   * (confirm vs un-exclude) based on the document's own `confirmed` state. */
  onPickDocument: (doc: EligibleDocument) => Promise<void>;
  /** "Add without a proof" — date, description, amount and direction all in one step. */
  onCreateManual: (entry: ManualCashEntry) => Promise<void>;
  /** "Upload a new proof now" — captures a brand-new receipt/invoice and uploads it with no
   * bank transaction to link to. */
  onUploadNew: (docType: DocType, file: File) => Promise<UploadNewCashOutcome>;
  /** Resolves a duplicate hit from onUploadNew — the caller confirms/un-excludes/refuses the
   * existing document depending on its current state. */
  onUseDuplicateDocument: (doc: EligibleDocument & { excluded_from_transaction_list?: boolean; bank_transaction_id?: string | null; bank_statement_transaction_id?: string | null }) => Promise<void>;
  /** Resolves the extraction-failure fallback from onUploadNew — the invoice ManualReceiptEntryDialog
   * just created still needs confirming, same as the happy path. */
  onManualEntryCreated: (invoice: { id: string }) => Promise<void>;
}

const todayStr = () => new Date().toISOString().slice(0, 10);

/** "Add new transaction" — the entry point at the top of the bank "View transactions" dialog for
 * adding one that isn't part of a live bank feed at all: from an already-uploaded receipt/
 * invoice, a brand-new one captured right now, or a genuinely proof-less manual entry. */
const AddCashTransactionDialog: React.FC<AddCashTransactionDialogProps> = ({
  open, onClose, eligibleDocuments, onPickDocument, onCreateManual, onUploadNew, onUseDuplicateDocument, onManualEntryCreated,
}) => {
  const { palette } = useTheme();
  const [mode, setMode] = useState<'choose' | 'pick-doc' | 'upload-new' | 'manual'>('choose');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── "From my already uploaded receipts and invoices" ──
  const [selectedDoc, setSelectedDoc] = useState<EligibleDocument | null>(null);

  // ── "Add without a proof" ──
  const [manualDate, setManualDate] = useState(todayStr());
  const [manualDescription, setManualDescription] = useState('');
  const [manualTotal, setManualTotal] = useState('');
  const [manualDirection, setManualDirection] = useState<'money_out' | 'money_in'>('money_out');

  // ── "Upload a new proof now" ──
  const [docType, setDocType] = useState<DocType | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [duplicateDoc, setDuplicateDoc] = useState<DuplicateCashDoc | null>(null);
  const [manualEntryFileInfo, setManualEntryFileInfo] = useState<any | null>(null);
  const [manualEntryError, setManualEntryError] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles[0]) setFile(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: docType === 'receipt'
      ? { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'], 'image/webp': ['.webp'], 'image/heic': ['.heic'], 'image/heif': ['.heif'] }
      : { 'application/pdf': ['.pdf'] },
    multiple: false,
  });
  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setFile(e.target.files[0]);
    e.target.value = '';
  };

  useEffect(() => {
    if (open) {
      setMode('choose');
      setSelectedDoc(null);
      setManualDate(todayStr());
      setManualDescription('');
      setManualTotal('');
      setManualDirection('money_out');
      setDocType(null);
      setFile(null);
      setDuplicateDoc(null);
      setManualEntryFileInfo(null);
      setManualEntryError(null);
      setError(null);
    }
  }, [open]);

  const handlePickConfirm = async () => {
    if (!selectedDoc) return;
    setSaving(true);
    setError(null);
    try {
      await onPickDocument(selectedDoc);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to add transaction');
    } finally {
      setSaving(false);
    }
  };

  const handleManualConfirm = async () => {
    if (!manualDate) return;
    setSaving(true);
    setError(null);
    try {
      await onCreateManual({ date: manualDate, description: manualDescription.trim(), total: manualTotal.trim(), direction: manualDirection });
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to add transaction');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async () => {
    if (!docType || !file) return;
    setSaving(true);
    setError(null);
    try {
      const outcome = await onUploadNew(docType, file);
      if (outcome.status === 'success') {
        onClose();
      } else if (outcome.status === 'duplicate') {
        setDuplicateDoc(outcome.invoice as any);
      } else {
        setManualEntryError(outcome.error);
        setManualEntryFileInfo(outcome.fileInfo);
      }
    } catch (e: any) {
      setError(e.message || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUseDuplicate = async () => {
    if (!duplicateDoc) return;
    setSaving(true);
    setError(null);
    try {
      await onUseDuplicateDocument(duplicateDoc as any);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to add transaction');
    } finally {
      setSaving(false);
    }
  };

  const backToChoose = () => {
    setMode('choose');
    setSelectedDoc(null);
    setDocType(null);
    setFile(null);
    setDuplicateDoc(null);
    setManualEntryFileInfo(null);
    setManualEntryError(null);
    setError(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {mode === 'choose' && 'How do you want to add a new transaction?'}
          {mode === 'pick-doc' && 'Which receipt or invoice is this?'}
          {mode === 'upload-new' && !docType && 'Add a new receipt or invoice'}
          {mode === 'upload-new' && docType && (duplicateDoc ? 'Already uploaded' : 'Upload a new proof')}
          {mode === 'manual' && 'Add without a proof'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {mode === 'choose' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              <Button
                variant="outlined"
                startIcon={<ReceiptIcon />}
                onClick={() => setMode('pick-doc')}
                disabled={eligibleDocuments.length === 0}
                sx={{ justifyContent: 'flex-start', py: 1.25 }}
              >
                From my already uploaded receipts and invoices{eligibleDocuments.length === 0 ? ' (none available)' : ''}
              </Button>
              <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setMode('upload-new')} sx={{ justifyContent: 'flex-start', py: 1.25 }}>
                Upload a new proof now
              </Button>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setMode('manual')} sx={{ justifyContent: 'flex-start', py: 1.25 }}>
                Add without a proof
              </Button>
            </Box>
          )}

          {mode === 'pick-doc' && (
            <Box sx={{ mt: 1 }}>
              <Autocomplete
                options={eligibleDocuments}
                value={selectedDoc}
                onChange={(_, val) => setSelectedDoc(val)}
                getOptionLabel={(o) => o.contractor_name || 'Untitled'}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props as any;
                  return (
                    <Box component="li" key={key} {...optionProps} sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Box>
                        <Typography variant="body2">{option.contractor_name || 'Untitled'}</Typography>
                        <Typography variant="caption" color="text.secondary">{option.invoice_date} · {option.document_type === 'Receipt' ? 'Receipt' : 'Invoice'}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>£{option.total}</Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => <TextField {...params} label="Search your uploads" placeholder="e.g. merchant name" autoFocus />}
              />
            </Box>
          )}

          {mode === 'upload-new' && (
            duplicateDoc ? (
              <Alert severity="info">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  This file's already been uploaded as <strong>{duplicateDoc.contractor_name || 'an existing document'}</strong>
                  {duplicateDoc.invoice_date ? ` (${duplicateDoc.invoice_date})` : ''}. Use it as this transaction?
                </Typography>
              </Alert>
            ) : !docType ? (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                <Button
                  fullWidth variant="outlined" startIcon={<ReceiptIcon />}
                  onClick={() => setDocType('receipt')}
                  sx={{ flexDirection: 'column', py: 2.5, borderRadius: 2, textTransform: 'none', borderColor: palette.tokens.hairline, color: palette.tokens.muted, gap: 0.5 }}
                >
                  Receipt<br /><Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>photo</Typography>
                </Button>
                <Button
                  fullWidth variant="outlined" startIcon={<InvoiceIcon />}
                  onClick={() => setDocType('invoice')}
                  sx={{ flexDirection: 'column', py: 2.5, borderRadius: 2, textTransform: 'none', borderColor: palette.tokens.hairline, color: palette.tokens.muted, gap: 0.5 }}
                >
                  Invoice<br /><Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>PDF</Typography>
                </Button>
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => { setDocType(null); setFile(null); }}
                  disabled={saving}
                  sx={{ textTransform: 'none', mb: 1.5, color: palette.tokens.muted }}
                >
                  ← Change type
                </Button>
                <UploadCaptureControls
                  docType={docType}
                  file={file}
                  isDragActive={isDragActive}
                  getRootProps={getRootProps}
                  getInputProps={getInputProps}
                  cameraInputRef={cameraInputRef}
                  onCameraCapture={handleCameraCapture}
                />
              </Box>
            )
          )}

          {mode === 'manual' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="When did this happen?" type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)}
                InputLabelProps={{ shrink: true }} inputProps={{ max: todayStr() }} fullWidth autoFocus
              />
              <TextField
                label="Description" value={manualDescription} onChange={(e) => setManualDescription(e.target.value)}
                placeholder="e.g. Rent payment" fullWidth
              />
              <TextField
                label="Amount (£)" type="number" value={manualTotal} onChange={(e) => setManualTotal(e.target.value)}
                placeholder="0.00" fullWidth
              />
              <ToggleButtonGroup
                value={manualDirection}
                exclusive
                fullWidth
                onChange={(_, val) => val && setManualDirection(val)}
              >
                <ToggleButton value="money_out" sx={{ textTransform: 'none', gap: 0.75 }}>
                  <MoneyOutIcon fontSize="small" /> Money out
                </ToggleButton>
                <ToggleButton value="money_in" sx={{ textTransform: 'none', gap: 0.75 }}>
                  <MoneyInIcon fontSize="small" /> Money in
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {mode !== 'choose' && (
            <Button onClick={backToChoose} disabled={saving} sx={{ mr: 'auto' }}>Back</Button>
          )}
          {mode === 'choose' && (
            <Button onClick={onClose} color="inherit" sx={{ mr: 'auto' }}>Cancel</Button>
          )}
          {mode === 'pick-doc' && (
            <Button variant="contained" onClick={handlePickConfirm} disabled={!selectedDoc || saving} startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}>
              Add transaction
            </Button>
          )}
          {mode === 'upload-new' && duplicateDoc && (
            <Button variant="contained" onClick={handleUseDuplicate} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}>
              Use this document
            </Button>
          )}
          {mode === 'upload-new' && !duplicateDoc && docType && (
            <Button variant="contained" onClick={handleUpload} disabled={!file || saving} startIcon={saving ? <CircularProgress size={16} /> : <UploadIcon />}>
              Upload
            </Button>
          )}
          {mode === 'manual' && (
            <Button variant="contained" onClick={handleManualConfirm} disabled={!manualDate || saving} startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}>
              Add transaction
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {manualEntryFileInfo && (
        <ManualReceiptEntryDialog
          open={!!manualEntryFileInfo}
          onClose={() => { setManualEntryFileInfo(null); setManualEntryError(null); }}
          onSuccess={async (invoice) => {
            setManualEntryFileInfo(null);
            setManualEntryError(null);
            await onManualEntryCreated(invoice);
            onClose();
          }}
          extractionError={manualEntryError || 'Automatic extraction failed'}
          fileInfo={manualEntryFileInfo}
        />
      )}
    </>
  );
};

export default AddCashTransactionDialog;
